import React, { useEffect } from 'react'
import { BaseView, BaseText } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'
import { styles } from './styles'
import { Action, ModalActionsProps } from './types'

export const ModalActions: React.FC<ModalActionsProps> = ({
  isVisible,
  onClose,
  actions,
}) => {
  const theme = useTheme()
  const [shouldRender, setShouldRender] = React.useState(false)
  const [isAnimating, setIsAnimating] = React.useState(false)

  // Управляем рендерингом для плавной анимации как в RN Modal
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true)
      // Небольшая задержка для запуска fade анимации как в RN Modal
      const animationTimer = setTimeout(() => setIsAnimating(true), 10)
      return () => clearTimeout(animationTimer)
    } else {
      setIsAnimating(false)
      // Задержка перед размонтированием для завершения fade анимации
      const timer = setTimeout(() => setShouldRender(false), 300)
      return () => clearTimeout(timer)
    }
  }, [isVisible])

  // Блокируем скролл body при открытии модального окна
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isVisible])

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        onClose()
      }
    }

    if (isVisible) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isVisible, onClose])

  if (!shouldRender) return null

  return (
    <BaseView
      style={{
        ...styles.modalOverlay,
        opacity: isAnimating ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transition: 'opacity 0.3s ease-out',
        // Backdrop с плавным блюром как в RN BlurView
        backgroundColor: isAnimating
          ? 'rgba(0, 0, 0, 0.5)'
          : 'rgba(0, 0, 0, 0)',
        backdropFilter: isAnimating ? 'blur(10px)' : 'blur(0px)',
        WebkitBackdropFilter: isAnimating ? 'blur(10px)' : 'blur(0px)',
        // Плавные переходы для всех свойств
        transitionProperty:
          'opacity, backdrop-filter, -webkit-backdrop-filter, background-color',
        transitionDuration: '0.3s',
        transitionTimingFunction: 'ease-out',
      }}
      onClick={onClose}
    >
      {/* Modal Content - как в React Native */}
      <BaseView
        style={{
          ...styles.content,
          backgroundColor: theme.colors.background.cardItemRoot,
          opacity: isAnimating ? 1 : 0,
          transition: 'opacity 0.3s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {actions.map((action, index) => (
          <React.Fragment key={action.label}>
            <BaseView
              style={styles.actionItem}
              className="hover:bg-gray-100 dark:hover:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700"
              onClick={() => {
                action.action()
                onClose()
              }}
            >
              <BaseText
                fontSize={16}
                fontFamily="semibold"
                style={{
                  color: action.isDestructive
                    ? theme.colors.label.failed
                    : theme.colors.label.primary,
                  textAlign: 'center',
                }}
              >
                {action.label}
              </BaseText>
            </BaseView>
            {index < actions.length - 1 && (
              <BaseView
                style={{
                  ...styles.separator,
                  backgroundColor: theme.colors.background.secondaryGray,
                }}
              />
            )}
          </React.Fragment>
        ))}
      </BaseView>
    </BaseView>
  )
}

export type { Action, ModalActionsProps }
